import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Display } from '../../components/widgets/Display';
import { faMagnifyingGlass, faSliders } from '@fortawesome/pro-light-svg-icons';
import { ChangeEvent, useCallback, useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { faBootHeeled, faCheck, faSpinner, faUserPlus } from '@fortawesome/pro-solid-svg-icons';
import { faShare } from '@fortawesome/free-solid-svg-icons';
import { PlaycastContext } from '../../components/PlaycastWrapper';
import { InviteButton } from '../../components/widgets/SendInvitePanel';
import { QueueOptionsV2 } from '../../components/widgets/QueueOptionsV2';

const findIndex = (array: any[], value: any) => {
  return array.findIndex((item) => item === value);
};

type PlayerItem = {
  name: string;
  playerId: string;
  isWaiting: boolean;
  playCount: number;
  queuePosition: number;
};

type QueueItemProps = {
  player: PlayerItem;
  onAccept: (playerId: string) => void;
  onReject: (playerId: string) => void;
  onQueueOrderChange: (playerId: string, newPosition: number) => void;
  maxQueuePosition: number;
};

const QueueItemComponent = ({ player, onAccept, onReject, onQueueOrderChange, maxQueuePosition }: QueueItemProps) => {
  const [tempValue, setTempValue] = useState(player.queuePosition.toString());

  const handleQueueOrderChange = (e: ChangeEvent<HTMLInputElement>) => {
    const numericValue = Number(e.target.value);
    if (isNaN(numericValue) || !Number.isInteger(numericValue)) {
      return;
    }

    setTempValue(e.target.value);
  };

  const handleBlur = () => {
    const parsed = parseInt(tempValue, 10);
    if (isNaN(parsed)) {
      // If not a number, revert to last valid value
      setTempValue(player.queuePosition.toString());
      return;
    }
    const newPosition = Math.min(Math.max(1, parsed), maxQueuePosition);
    setTempValue(newPosition.toString());
    if (newPosition !== player.queuePosition) {
      onQueueOrderChange(player.playerId, newPosition);
    }
  };

  // Update temp value when player.queuePosition changes externally
  useEffect(() => {
    setTempValue(player.queuePosition.toString());
  }, [player.queuePosition]);

  return (
    <div
      data-testid={`queue-item-${player.name}`}
      className={clsx('border-primary rounded-lg border-2 bg-slate-700 p-2', player.isWaiting && 'opacity-50')}
    >
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          {!player.isWaiting ? (
            <input
              data-testid={`queue-position-input-${player.name}`}
              type='text'
              name={player.playerId}
              className='bg-darkest flex size-8 items-center justify-center rounded-md border-0 p-0 text-center font-bold text-white'
              value={tempValue}
              onChange={handleQueueOrderChange}
              onBlur={handleBlur}
              min={1}
              max={maxQueuePosition}
            />
          ) : (
            <FontAwesomeIcon icon={faSpinner} className='size-6 animate-spin text-emerald-400' />
          )}
          <span data-testid={`player-name-${player.name}`} className='text-white'>
            {player.name}
          </span>
        </div>
        <div className='flex items-center gap-2 text-white'>
          <button
            data-testid={`accept-button-${player.name}`}
            disabled={player.isWaiting}
            className='bg-primary enabled:hover:bg-primary/80 enabled:active:bg-primary/60 flex size-8 items-center justify-center rounded disabled:opacity-50 disabled:hover:cursor-not-allowed'
            onClick={() => onAccept(player.playerId)}
          >
            <FontAwesomeIcon icon={player.isWaiting ? faCheck : faShare} className='size-4 text-emerald-400' />
          </button>
          <button
            data-testid={`reject-button-${player.name}`}
            disabled={player.isWaiting}
            className='bg-primary enabled:hover:bg-primary/80 enabled:active:bg-primary/60 flex size-8 items-center justify-center rounded disabled:opacity-50 disabled:hover:cursor-not-allowed'
            onClick={() => onReject(player.playerId)}
          >
            <FontAwesomeIcon icon={faBootHeeled} className='size-3.5 -rotate-[30deg] text-orange-600' />
          </button>
        </div>
      </div>
    </div>
  );
};

export const Lobby = () => {
  const context = useContext(PlaycastContext);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [allPlayers, setAllPlayers] = useState<PlayerItem[]>([]);
  const [filteredList, setFilteredList] = useState<PlayerItem[]>([]);

  const filterPlayers = useCallback((players: PlayerItem[], search: string) => {
    if (!search.trim()) {
      setFilteredList(players);
      return;
    }

    const searchLower = search.toLowerCase();
    const filtered = players.filter((player) => player.name.toLowerCase().includes(searchLower));
    setFilteredList(filtered);
  }, []);

  const processLobbyState = useCallback(() => {
    const knocks = context.stateStores.lobbyStateV2.knocks;
    const invitation = context.stateStores.invitationStateV2;

    if (invitation.type !== 'lobby') {
      return;
    }

    const pendingOut: PlayerItem[] = knocks.pending.map((player, index) => {
      return {
        playerId: player.playerId,
        isWaiting: false,
        name: player.displayName,
        playCount: player.playCount ?? 0,
        queuePosition: index + 1,
      };
    });

    const acceptedOut: PlayerItem[] = knocks.accepted.map((player, index) => {
      return {
        playerId: player.playerId,
        isWaiting: true,
        name: player.displayName,
        playCount: player.playCount ?? 0,
        queuePosition: index + 1,
      };
    });

    const newPlayerList = [...acceptedOut, ...pendingOut];
    setAllPlayers(newPlayerList);
    filterPlayers(newPlayerList, searchTerm);
  }, [context.stateStores.invitationStateV2, context.stateStores.lobbyStateV2.knocks, filterPlayers, searchTerm]);

  useEffect(() => {
    processLobbyState();
  }, [processLobbyState]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const checkForScrollbar = () => {
      const hasVerticalScrollbar = container.scrollHeight > container.clientHeight;
      setHasScrollbar(hasVerticalScrollbar);
    };

    checkForScrollbar();

    const resizeObserver = new ResizeObserver(checkForScrollbar);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    filterPlayers(allPlayers, newSearchTerm);
  };

  const handleAccept = (playerId: string) => {
    context.targetFunctions.acceptKnock(playerId);
  };

  const handleReject = (playerId: string) => {
    context.targetFunctions.rejectKnock(playerId);
  };

  const handleLobbySwitch = () => {
    const invitation = context.stateStores.invitationStateV2;
    if (invitation.type !== 'lobby' || !invitation.manifest.lobbyOptions) return;

    invitation.manifest.lobbyOptions.canJoin = !invitation.manifest.lobbyOptions.canJoin;
    context.targetFunctions.updateLobbySettings(invitation);
  };

  const handleQueueOrderChange = useCallback(
    (playerId: string, newPosition: number) => {
      // Find the player in the filtered list
      const playerIndex = filteredList.findIndex((p) => p.playerId === playerId);
      if (playerIndex === -1) return;

      // Clamp newPosition to valid range
      newPosition = Math.max(1, Math.min(newPosition, filteredList.length));

      // Remove the player from their current position
      const newFilteredList = [...filteredList];
      const [movedPlayer] = newFilteredList.splice(playerIndex, 1);

      // Insert the player at the new position (array is 0-indexed)
      newFilteredList.splice(newPosition - 1, 0, movedPlayer);

      // Reassign queue positions to be sequential
      const updatedList = newFilteredList.map((p, idx) => ({
        ...p,
        queuePosition: idx + 1,
      }));

      // Update the order in the backend/store
      const fullListOrder = updatedList.map((p) => p.playerId);
      context.targetFunctions.reorderLobby(fullListOrder);

      setFilteredList(updatedList);
      setAllPlayers(updatedList);
    },
    [context.targetFunctions, filteredList],
  );

  return (
    <>
      <div className='hidden lg:block'>
        <Display showPicker={false} />
      </div>
      <div className='flex flex-1 flex-col gap-4 overflow-hidden'>
        <div className='flex gap-2'>
          <button
            data-testid='lobby-status-button'
            className='bg-primary hover:bg-primary/80 active:bg-primary/60 min-w-20 rounded px-4 text-white'
            onClick={handleLobbySwitch}
          >
            {context.stateStores.invitationStateV2.type === 'lobby' && context.stateStores.invitationStateV2.manifest.lobbyOptions.canJoin
              ? 'Open'
              : 'Closed'}
          </button>
          <QueueOptionsV2 />
          <div className='relative w-full'>
            <FontAwesomeIcon icon={faMagnifyingGlass} className='absolute left-2 top-1/2 z-10 -translate-y-1/2 transform text-gray-500' />
            <input
              data-testid='search-input'
              type='text'
              placeholder='Search...'
              value={searchTerm}
              onChange={handleSearch}
              className='border-primary focus-visible:border-primary placeholder:text-md h-10 w-full rounded border-2 bg-gray-800 px-3 pl-7 text-white focus-visible:outline-none focus-visible:ring-0'
            />
          </div>
        </div>
        <div data-testid='lobby-container' className='border-primary bg-darker relative flex-1 overflow-hidden rounded-lg border-2'>
          <div className='relative z-50 h-full p-2'>
            <div
              ref={scrollContainerRef}
              data-testid='queue-list'
              className={clsx(
                'h-full overflow-y-auto',
                hasScrollbar ? 'pr-2' : '',
                '[&::-webkit-scrollbar-thumb]:rounded-lg [&::-webkit-scrollbar-thumb]:bg-slate-600',
                '[&::-webkit-scrollbar-track]:rounded-md [&::-webkit-scrollbar-track]:bg-slate-800',
                '[&::-webkit-scrollbar-track]:shadow-[inset_0_0_0_2px_rgb(var(--colors-primary))]',
                '[&::-webkit-scrollbar]:w-8 [&::-webkit-scrollbar]:rounded-md [&::-webkit-scrollbar]:bg-slate-800',
              )}
            >
              <div className='space-y-2'>
                {/* {Array.from({ length: 10 }).map((_, index) => (
                  <QueueItemComponent
                    key={index + 1}
                    player={{
                      playerId: index.toString(),
                      name: index.toString(),
                      isWaiting: false,
                      playCount: 0,
                      queuePosition: index + 1,
                    }}
                    onAccept={handleAccept}
                    onReject={handleReject}
                    onQueueOrderChange={handleQueueOrderChange}
                    maxQueuePosition={filteredList.length}
                  />
                ))} */}

                {filteredList
                  .sort((a, b) => a.queuePosition - b.queuePosition)
                  .map((player) => (
                    <QueueItemComponent
                      key={player.playerId}
                      player={player}
                      onAccept={handleAccept}
                      onReject={handleReject}
                      onQueueOrderChange={handleQueueOrderChange}
                      maxQueuePosition={filteredList.length}
                    />
                  ))}
                <div className='hidden lg:flex'>
                  <InviteButton
                    data-testid='invite-button'
                    text='Invite Your Friends To Join'
                    icon={faUserPlus}
                    tooltip={
                      context.stateStores.hostState.status === 'streaming'
                        ? !(
                            context.stateStores.invitationStateV2.type === 'lobby' &&
                            context.stateStores.invitationStateV2.manifest.lobbyOptions.canJoin
                          )
                          ? 'Friends cannot join when lobby is closed'
                          : undefined
                        : 'Start a session to invite friends!'
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <span className='text-dark absolute bottom-2 left-1/2 -translate-x-1/2 text-7xl md:text-9xl'>Lobby</span>
        </div>
      </div>
    </>
  );
};

